<!-- components/Pebble/Toast/index.vue -->
<template>
  <Teleport to="body">
    <Transition name="toast-slide">
      <div
        v-if="visible"
        class="fixed flex items-start w-[350px] p-4 shadow-lg z-9999 border-l-4 text-wrap text-sm rounded-lg bg-white dark:bg-pb_gray_800 dark:text-pb_gray_100 transition-all duration-300 ease-in-out"
        :class="[typeClasses[type], { 'border border-l-4': bordered }]"
        :style="dynamicStyles"
      >
        <div class="flex items-center justify-center mr-3 shrink-0 mt-0.5">
          <CheckCircle v-if="type === 'success'" color="green"/>

          <XCircle v-else-if="type === 'error'" color="red"/>

          <CircleAlert v-else-if="type === 'warning'" color="yellow"/>

          <Info v-else-if="type === 'info'" color="blue"/>
        </div>

        <div class="flex-1 min-w-0">
          <div class="font-semibold text-sm leading-5 mb-1">{{ title }}</div>
          <div v-if="message" class="text-xs leading-4 opacity-90">{{ message }}</div>
        </div>

        <button
          class="flex items-center justify-center p-1.5 ml-3 text-current transition-opacity duration-200 bg-transparent border-none cursor-pointer opacity-70 hover:opacity-100 shrink-0"
          aria-label="Close notification"
          @click="close"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import {CheckCircle, CircleAlert, XCircle, Info} from "lucide-vue-next";
const props = defineProps({
  type: {
    type: String,
    default: "success",
    validator: (value: string) =>
      ["success", "error", "warning", "info"].includes(value),
  },
  title: {
    type: String,
    required: true,
  },
  message: {
    type: String,
    default: "",
  },
  duration: {
    type: Number,
    default: 5000,
  },
  position: {
    type: Object,
    default: () => ({ top: 20, right: 20 }),
  },
  bordered: {
    type: Boolean,
    default: false,
  },
  index: {
    type: Number,
    default: 0,
  },
  id: {
    type: [String, Number],
    required: true,
  },
});

const typeClasses: Record<string, string> = {
  success: "bg-emerald-50 text-emerald-800 border-emerald-400 dark:bg-emerald-900/90 dark:text-emerald-200 dark:border-emerald-500",
  error: "bg-red-50 text-red-800 border-red-400 dark:bg-red-900/90 dark:text-red-200 dark:border-red-500",
  warning: "bg-amber-50 text-amber-800 border-amber-400 dark:bg-amber-900/90 dark:text-amber-200 dark:border-amber-500",
  info: "bg-blue-50 text-blue-800 border-blue-400 dark:bg-blue-900/90 dark:text-blue-200 dark:border-blue-500",
};

const emit = defineEmits(["close"]);
const visible = ref(false);
let timer: ReturnType<typeof setTimeout> | null = null;

// Calculate dynamic position with consistent spacing
const dynamicStyles = computed(() => {
  return {
    top: `${props.position.top}px`,
    right: `${props.position.right}px`,
    // Add a smooth transition for position changes
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  };
});

onMounted(() => {
  visible.value = true;
  if (props.duration > 0) {
    timer = setTimeout(() => {
      close();
    }, props.duration);
  }
});

onBeforeUnmount(() => {
  if (timer) clearTimeout(timer);
});

function close() {
  visible.value = false;
  setTimeout(() => {
    emit("close", props.id);
  }, 300); // Wait for the transition to finish
}
</script>

<style scoped>
.toast-slide-enter-active {
  transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

.toast-slide-leave-active {
  transition: all 0.3s cubic-bezier(0.55, 0.055, 0.675, 0.19);
}

.toast-slide-enter-from {
  transform: translateX(calc(100% + 20px));
  opacity: 0;
}

.toast-slide-leave-to {
  transform: translateX(calc(100% + 20px));
  opacity: 0;
}

/* Ensure smooth position transitions */
.toast-slide-enter-active,
.toast-slide-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}
</style>